import type { CityConfig } from "@/pages/city-template/types";

export const medfordConfig: CityConfig = {
  city: "Medford",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "medford",
  route: "/cities/medford",
  title: "Emergency Passport Medford, OR | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Medford, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Southern Oregon travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Medford, Oregon",
  heroSubtitle: "Heading to the Oregon Shakespeare Festival in Ashland or a wine tour in the Rogue Valley? Don't let an expired passport ruin your plans. Fast Passport Center helps Medford and Southern Oregon travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Medford%20Oregon%20downtown%20with%20Rogue%20Valley%20landscape%2C%20rolling%20hills%20and%20vineyards%20in%20background%2C%20Table%20Rocks%20visible%2C%20warm%20golden%20hour%20light%20over%20southern%20Oregon%20countryside%2C%20pine%20trees%20and%20agricultural%20fields%2C%20photorealistic%20landscape%20photography%2C%20vibrant%20Southern%20Oregon%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=medford-hero-01&orientation=landscape",
  heroImageAlt: "Medford Oregon downtown with Rogue Valley vineyards and Table Rocks at golden hour",
  geoPosition: "42.3265;-122.8756",
  geoPlacename: "Medford, Oregon",
  lastModified: "2026-05-29",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, San Francisco, CA 94102",
    distance: "About 360 miles south — 6 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Medford", zips: "97501–97504" },
    { name: "Ashland", zips: "97520" },
    { name: "Grants Pass", zips: "97526–97528" },
    { name: "Central Point", zips: "97502" },
    { name: "Eagle Point", zips: "97524" },
    { name: "Talent", zips: "97540" },
    { name: "Phoenix", zips: "97535" },
    { name: "Rogue River", zips: "97537" },
    { name: "Jacksonville", zips: "97530" },
    { name: "White City", zips: "97503" },
    { name: "Shady Cove", zips: "97539" },
    { name: "Gold Hill", zips: "97525" },
    { name: "Butte Falls", zips: "97522" },
    { name: "Applegate", zips: "97530" },
    { name: "Murphy", zips: "97533" },
    { name: "Williams", zips: "97544" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in Medford in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Medford", desc: "FedEx and UPS locations throughout Medford and Grants Pass offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — especially important for last-minute passport needs before festival or wine country travel." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Susan H.",
      location: "Medford, OR",
      rating: 5,
      text: "My passport expired right before our anniversary trip to Napa and then Vancouver. Fast Passport Center got my renewal done in 6 days. I shipped from the UPS on Riverside Ave in Medford. The whole process was seamless.",
      service: "Passport Renewal",
    },
    {
      name: "Carlos R.",
      location: "Ashland, OR",
      rating: 5,
      text: "Our daughter needed a passport for a school exchange to France. We live in Ashland and the local post office was booked weeks out. Fast Passport Center gave us a clear plan, we did the acceptance step at the Jackson County Clerk, and had the passport in 8 days.",
      service: "Child Passport",
    },
    {
      name: "Linda P.",
      location: "Grants Pass, OR",
      rating: 5,
      text: "Lost my passport before a business trip to Toronto. I was panicking because I live in Grants Pass and thought I'd have to drive to San Francisco. Fast Passport Center handled everything by courier — I never left town. Five days and it was in my mailbox.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "medford-main",
      name: "Medford Main Post Office",
      type: "USPS Passport Acceptance",
      address: "325 N Riverside Ave",
      city: "Medford",
      state: "OR",
      zip: "97501",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm, Sat 9am–1pm",
      distance: "Downtown Medford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Medford. Appointment required — book online well in advance. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "medford-east",
      name: "Medford East Station",
      type: "USPS Passport Acceptance",
      address: "2320 E Barnett Rd",
      city: "Medford",
      state: "OR",
      zip: "97504",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "East Medford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "East Medford passport acceptance. Confirm appointment and photo service availability before visiting.",
      icon: "ri-mail-line",
    },
    {
      id: "ashland-post",
      name: "Ashland Post Office",
      type: "USPS Passport Acceptance",
      address: "1200 Idaho St",
      city: "Ashland",
      state: "OR",
      zip: "97520",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Ashland — about 15 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Ashland option for southern Jackson County residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "jackson-county",
      name: "Jackson County Clerk",
      type: "County Passport Acceptance",
      address: "10 S Oakdale Ave",
      city: "Medford",
      state: "OR",
      zip: "97501",
      phone: "541-774-6159",
      hours: "Mon–Fri 8am–5pm (by appointment)",
      distance: "Downtown Medford — County Courthouse",
      website: "https://jacksoncountyor.gov/county-clerk",
      websiteLabel: "Jackson County Clerk",
      notes: "County clerk passport acceptance in Medford. Appointment required — often faster scheduling than USPS. Call ahead to confirm passport services.",
      icon: "ri-government-line",
    },
    {
      id: "grants-pass-post",
      name: "Grants Pass Post Office",
      type: "USPS Passport Acceptance",
      address: "142 N 2nd St",
      city: "Grants Pass",
      state: "OR",
      zip: "97526",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Grants Pass — about 30 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Josephine County option for northwestern residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "central-point-post",
      name: "Central Point Post Office",
      type: "USPS Passport Acceptance",
      address: "625 E Pine St",
      city: "Central Point",
      state: "OR",
      zip: "97502",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Central Point — about 4 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Central Point option for northern Medford area residents. Confirm appointment availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Medford before international travel?",
      a: "Yes. Through Fast Passport Center, Medford travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — don't wait until the last minute.",
    },
    {
      q: "How fast can I renew my passport in Medford?",
      a: "Fast passport renewal in Medford is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my trip to Vancouver or Europe?",
      a: "If your passport expires before your trip to Vancouver, Europe, or any international destination, start now. Most countries require 6 months of passport validity beyond your travel dates. Fast Passport Center can expedite your passport renewal so you don't miss your flight out of MFR.",
    },
    {
      q: "Can I get a same-day passport appointment near Medford?",
      a: "Same-day passport appointments at the regional passport agency in San Francisco are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Medford, Fast Passport Center is the faster and more predictable option — no 6-hour drive to San Francisco required.",
    },
    {
      q: "Can I expedite a child passport in Medford?",
      a: "Yes. Child passport expediting in Medford is possible through Fast Passport Center, but child passports (for minors 15 and under) do require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it lost or stolen and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps and lost passports cannot be used even if found later.",
    },
    {
      q: "Does Medford have a passport agency?",
      a: "Medford, Oregon does not have a regional passport agency. The closest regional agencies are the San Francisco Passport Agency in California (about 360 miles south) and the Seattle Passport Agency in Washington (about 430 miles north). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Medford residents.",
    },
    {
      q: "How long does a standard passport take in Medford?",
      a: "If you apply through a standard post office acceptance facility in Medford, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers Medford travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I get a passport renewal near me in Medford without visiting an office?",
      a: "Yes. Most Medford-area adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in Medford or Grants Pass.",
    },
    {
      q: "What airports do Medford passport holders typically fly from?",
      a: "Rogue Valley International-Medford Airport (MFR) is the primary airport for Medford and Southern Oregon travelers, offering direct flights to Seattle, Portland, Denver, Salt Lake City, Los Angeles, and San Francisco. The busiest travel seasons are June–September (festival and wine season) and December–January — plan passport needs well in advance.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Medford North", address: "2110 W Main St, Medford, OR 97501", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Medford East", address: "1201 E Barnett Rd, Medford, OR 97504", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Medford Downtown", address: "106 W Main St, Medford, OR 97501", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "The UPS Store — Medford", address: "1101 E Jackson St, Medford, OR 97504", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walmart — Medford", address: "1360 Center Dr, Medford, OR 97501", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Ashland", address: "545 Siskiyou Blvd, Ashland, OR 97520", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Grants Pass", address: "1638 NE 7th St, Grants Pass, OR 97526", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Grants Pass", address: "127 NE Terry Ln, Grants Pass, OR 97526", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Walmart — Grants Pass", address: "135 NE Terry Ln, Grants Pass, OR 97526", hours: "Daily 7 AM – 11 PM" },
    { name: "The UPS Store — Ashland", address: "585 A St, Ashland, OR 97520", hours: "Mon–Fri 8 AM – 6 PM" },
  ],
  dmvLocations: [
    {
      name: "Medford DMV Office",
      address: "132 E Barnett Rd, Medford, OR 97501",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Primary DMV office for Medford and Jackson County. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Ashland DMV Office",
      address: "600 Washington St, Ashland, OR 97520",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Ashland DMV option for southern Jackson County residents. Appointment recommended.",
      featured: false,
    },
    {
      name: "Grants Pass DMV Office",
      address: "1848 NW Washington Blvd, Grants Pass, OR 97526",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Josephine County option for Grants Pass and northwestern residents. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "MFR",
      name: "Rogue Valley International-Medford Airport",
      distance: "Primary airport for Medford — 3 miles north of downtown",
      address: "1000 Terminal Loop Pkwy, Medford, OR 97504",
      note: "MFR serves Southern Oregon with direct flights to Seattle, Portland, Denver, Salt Lake City, Los Angeles, San Francisco, and Phoenix. The busiest travel seasons are June–September (festival and wine season) and December–January — plan passport needs well in advance.",
      flightUrl: "https://www.co.jackson.or.us/Departments/Airport",
      flightLabel: "MFR Airport Website",
      featured: true,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Alternative international hub — about 275 miles north",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Some Medford travelers use PDX for additional international flight options. Passport required for all international departures.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from MFR in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Medford and Southern Oregon.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Medford and Southern Oregon travelers renew fast — passport expired before a wine country or festival trip is one of our most common urgent requests.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Medford", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Medford travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Medford families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Medford residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines. Same-day passport help for Medford travelers.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Medford, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Medford residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Medford, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Medford travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Medford families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Medford, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Medford travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip, especially for travel during June–September and December–January." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before your documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission, critical for last-minute passport renewal in Medford." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Medford runs 6–13 weeks. If your trip to Canada, Europe, or Asia is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — especially for international flights out of MFR. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Medford travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Medford", description: "Drop off at any FedEx or UPS in Medford or Grants Pass — most locations are open evenings. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in San Francisco", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to San Francisco (6 hours each way)", fpc: "Ship documents overnight from Medford" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Eugene", route: "/cities/eugene" },
    { name: "Rush passport service in Ashland", route: "#" },
    { name: "Emergency passport help near Grants Pass", route: "#" },
    { name: "Fast passport renewal for Southern Oregon travelers", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Medford or Grants Pass — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeDistance: "Serving Medford and all of Southern Oregon remotely",
  serviceAreaIntro: "Whether you are in downtown Medford, Ashland, Grants Pass, Central Point, Eagle Point, or anywhere in the greater Rogue Valley, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Southern Oregon zip codes. Anywhere in Jackson or Josephine County — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Medford as simple as possible. Most Medford clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Medford travelers. Fast Passport Center reviews your documents before submission to catch these issues before they become problems.",
  testimonialsIntro: "Real reviews from Medford, Ashland, and Grants Pass travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Medford and Southern Oregon travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  dmvWebsite: "https://www.oregon.gov/odot/dmv",
  dmvLabel: "Oregon DMV — Official Website",
};